// @ts-nocheck
import { useEffect, useState } from 'react'
import { Smartphone, X, AlertCircle, CheckCircle2 } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { formatPhoneNumberForDisplay } from '../utils/phoneNumberHelper'

/**
 * モバイル直接着信 (mobile-direct) 設定
 *
 * この番号への着信を、指定ユーザーのモバイルアプリ (ガンモ) に直接鳴らす。
 * 有効にすると Web のキューには入らない (不応答時のフォールバックを除く)。
 * 設計: docs/MOBILE_RECEPTION_APP_DESIGN.md §4.4 / §4.5
 */
export default function MobileRoutingSettings({ phoneNumber, onClose }) {
  const api = useApi()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)
  const [users, setUsers] = useState([])
  const [enabled, setEnabled] = useState(false)
  const [ringAll, setRingAll] = useState(true)
  const [mobileTargets, setMobileTargets] = useState([])
  const [dialTimeout, setDialTimeout] = useState(25)
  const [fallback, setFallback] = useState('queue')

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        const usersData = await api.get('/api/users')
        setUsers(usersData.items || [])

        const data = await api.get(
          `/api/phone-numbers/${phoneNumber.phoneNumberId}/mobile-routing`
        )
        const mr = data.mobileRouting
        if (mr) {
          setEnabled(mr.enabled === true)
          setRingAll(mr.ringAll === true || !(mr.mobileTargets || []).length)
          setMobileTargets(mr.mobileTargets || [])
          setDialTimeout(mr.dialTimeout || 25)
          setFallback(mr.fallback === 'message' ? 'message' : 'queue')
        }
      } catch (err) {
        console.error('モバイル着信設定取得エラー:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    if (phoneNumber?.phoneNumberId) {
      fetchData()
    }
  }, [phoneNumber?.phoneNumberId, api])

  const toggleTarget = (userId) => {
    setMobileTargets((prev) => {
      if (prev.includes(userId)) return prev.filter((id) => id !== userId)
      if (prev.length >= 10) return prev // 同時鳴動は最大 10 人 (Twilio の制限)
      return [...prev, userId]
    })
  }

  const handleSave = async () => {
    if (enabled && !ringAll && mobileTargets.length === 0) {
      setError('個別選択の場合は鳴らすユーザーを 1 人以上選択してください')
      return
    }

    try {
      setSaving(true)
      setError(null)

      await api.put(`/api/phone-numbers/${phoneNumber.phoneNumberId}/mobile-routing`, {
        enabled,
        ringAll,
        mobileTargets: enabled && !ringAll ? mobileTargets : [],
        dialTimeout,
        fallback,
      })

      setSaved(true)
      setTimeout(() => {
        setSaved(false)
        onClose()
      }, 1500)
    } catch (err) {
      console.error('保存エラー:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-blue-600" />
          モバイル着信設定
        </h3>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {formatPhoneNumberForDisplay(phoneNumber.phoneNumber)}
        {phoneNumber.name ? ` (${phoneNumber.name})` : ''} への着信を、
        選択したユーザーのモバイルアプリに直接鳴らします。
      </p>

      <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-700 dark:text-amber-300">
        有効にすると、この番号は Web の着信キューに入らなくなります
        (不応答時の動作が「キューへ」の場合を除く)。
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {saved && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          保存しました
        </div>
      )}

      {/* 有効/無効 */}
      <label className="flex items-center gap-3 mb-5 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="w-5 h-5 rounded text-blue-600"
        />
        <span className="text-sm font-medium text-gray-900 dark:text-white">
          モバイル直接着信を有効にする
        </span>
      </label>

      {enabled && (
        <>
          {/* 鳴らすユーザー */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              鳴らすユーザー
              <span className="ml-2 text-xs font-normal text-gray-500">
                対象者の端末が同時に鳴り、最初に応答した人につながります
              </span>
            </label>
            <div className="mb-3 text-xs text-gray-500 dark:text-gray-400">
              ※ 同時に鳴らせるのは最大 10 人です (Twilio の制限)。
              全員モードで 10 人を超える組織では先頭 10 人のみ鳴ります。
            </div>
            <div className="space-y-2 mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="ringMode"
                  checked={ringAll}
                  onChange={() => setRingAll(true)}
                  className="text-blue-600"
                />
                <span className="text-sm text-gray-900 dark:text-white">
                  全員に鳴らす (推奨)
                  <span className="ml-1 text-xs text-gray-500">
                    アプリにログインしている人だけが実際に鳴ります。鳴らしたくない人はログアウト
                  </span>
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="ringMode"
                  checked={!ringAll}
                  onChange={() => setRingAll(false)}
                  className="text-blue-600"
                />
                <span className="text-sm text-gray-900 dark:text-white">
                  選択したユーザーのみ
                  {!ringAll && mobileTargets.length > 0 && (
                    <span className="ml-1 text-xs text-gray-500">({mobileTargets.length}/10 人)</span>
                  )}
                </span>
              </label>
            </div>
            {!ringAll && (
            <div className="max-h-52 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
              {users.length === 0 ? (
                <div className="p-3 text-sm text-gray-500">ユーザーがいません</div>
              ) : (
                users.map((user) => (
                  <label
                    key={user.userId}
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <input
                      type="checkbox"
                      checked={mobileTargets.includes(user.userId)}
                      onChange={() => toggleTarget(user.userId)}
                      className="w-4 h-4 rounded text-blue-600"
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {user.displayName || user.name || user.email}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{user.email}</div>
                    </div>
                  </label>
                ))
              )}
            </div>
            )}
          </div>

          {/* 呼出タイムアウト */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              呼出時間 (秒)
            </label>
            <input
              type="number"
              min={5}
              max={120}
              value={dialTimeout}
              onChange={(e) => setDialTimeout(Number(e.target.value))}
              className="w-28 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
            <span className="ml-2 text-xs text-gray-500">5〜120 秒</span>
          </div>

          {/* フォールバック */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              誰も応答しなかったとき
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="fallback"
                  checked={fallback === 'queue'}
                  onChange={() => setFallback('queue')}
                  className="text-blue-600"
                />
                <span className="text-sm text-gray-900 dark:text-white">
                  Web の着信キューに入れる
                  <span className="ml-1 text-xs text-gray-500">(Web オペレータが対応できる)</span>
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="fallback"
                  checked={fallback === 'message'}
                  onChange={() => setFallback('message')}
                  className="text-blue-600"
                />
                <span className="text-sm text-gray-900 dark:text-white">
                  アナウンスを流して切断する
                </span>
              </label>
            </div>
          </div>
        </>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          キャンセル
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
        >
          {saving ? '保存中...' : '保存'}
        </button>
      </div>
    </div>
  )
}
